/*
 * sharedFunctions.tsx (AbstractECommerce)
 *
 * Copyright © 2023 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rafael Rodrigues, 2023
 *
 * @file sharedFunctions.tsx
 * @author Rafael Rodrigues
 * @copyright 2023 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { SubscriptionStatus } from '@abstract/abstractwebcommon-shared/enum/ecommerce/braintreeSubscriptionStatus';
import { ISubscription } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/Subscription';
import { differenceInDays } from 'date-fns';

/**
 * Shared functions for the subscription entity.
 */
export class SubscriptionSharedFucntions {
  /**
   * Refund Subscription function can only be executed with it's within specific days window defined in Settings page since the subscription creation.
   * @param subscriptionDetails ISubscription.
   * @param refundWindow Refund window defined in Settings page.
   * @param returns boolean.
   */
  static isWithinSubscriptionRefundWindow = (
    subscriptionDetails: ISubscription,
    refundWindow: number
  ): boolean => {
    const daysSinceSubscriptionStart: number = differenceInDays(
      new Date(),
      new Date(subscriptionDetails.createdAt!)
    );

    return daysSinceSubscriptionStart <= refundWindow ? true : false;
  };

  /**
   * Validates if user can use the Refund a subscription in Subscription page. Used for admin and non-admin users.
   * @param subscriptionDetails ISubscription.
   * @param refundWindow Refund window defined in Settings page.
   * @param isAdmin True when the current user is an admin, who can always refund regardless of prior refunds.
   * @param returns boolean.
   */
  static isRefundOptionAvailable = (
    subscription: ISubscription,
    refundWindow: number,
    isAdmin: boolean = false
  ): boolean =>
    subscription.localSubscriptionStatus === SubscriptionStatus.Active &&
    // NOTE: A user cannot request another refund for this subscription while one is already awaiting
    // NOTE: admin review. Admins keep their direct-refund button regardless of a pending request.
    (isAdmin || !subscription.pendingRefundRequest) &&
    this.isWithinSubscriptionRefundWindow(subscription, refundWindow);

  /**
   * Returns the reason the refund option is unavailable for a subscription, or null when it is
   * available. Used to show a tooltip explaining why the refund button is disabled.
   * @param subscription ISubscription.
   * @param refundWindow Refund window defined in Settings page.
   * @param isAdmin True when the current user is an admin, who can always refund.
   * @returns The disabled reason, or null when refunding is available.
   */
  static getRefundDisabledReason = (
    subscription: ISubscription,
    refundWindow: number,
    isAdmin: boolean = false
  ): RefundDisabledReason => {
    if (this.isRefundOptionAvailable(subscription, refundWindow, isAdmin)) {
      return null;
    }
    if (subscription.localSubscriptionStatus !== SubscriptionStatus.Active) {
      return 'notActive';
    }
    if (!isAdmin && subscription.pendingRefundRequest) {
      return 'pendingRequest';
    }
    if (!this.isWithinSubscriptionRefundWindow(subscription, refundWindow)) {
      return 'outsideWindow';
    }
    return null;
  };
}

/**
 * Reason the refund option is unavailable for a subscription, or null when it is available.
 */
export type RefundDisabledReason =
  | 'notActive' /**< The subscription is not active. */
  | 'pendingRequest' /**< A refund request is already awaiting admin review. */
  | 'outsideWindow' /**< The subscription is past the refund window. */
  | null;
